import { useEffect } from "react";
import { Link } from "react-router-dom";
import { BRAND } from "@/data/content";
import Footer from "@/components/site/Footer";

export function LegalLayout({ title, updated, children }) {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} | Dentro il Calice AI Winery`;
  }, [title]);

  return (
    <div className="min-h-screen bg-brand-cream grain-overlay">
      <div className="px-4 sm:px-8 lg:px-12 py-8 border-b border-brand-bordeaux/10">
        <Link to="/" className="flex flex-col leading-none w-fit">
          <span className="font-serif text-xl tracking-tight text-brand-bordeaux">{BRAND.name}</span>
          <span className="mt-0.5 text-[10px] uppercase tracking-editorial font-semibold text-brand-gold">
            {BRAND.division}
          </span>
        </Link>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-8 lg:px-12 py-16 lg:py-24">
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-bordeaux">{title}</h1>
        <p className="mt-3 font-mono text-xs text-brand-graphite/50">{updated}</p>
        <div className="mt-10 space-y-8 text-brand-graphite/80">{children}</div>
        <div className="mt-16">
          <Link to="/" className="text-sm text-brand-bordeaux underline hover:text-brand-wine">← Torna alla home</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Block({ h, children }) {
  return (
    <section>
      <h2 className="font-serif text-2xl text-brand-bordeaux">{h}</h2>
      <div className="mt-3 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function Li({ children }) {
  return <li className="ml-5 list-disc marker:text-brand-gold">{children}</li>;
}

const UPDATED = "Ultimo aggiornamento: 1 settembre 2026";

export function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" updated={UPDATED}>
      <Block h="1. Titolare del trattamento">
        <p>
          Il Titolare del trattamento è <strong>Stefano Cremonese</strong> — Piazza J.F. Kennedy 2, 37060 Castel d'Azzano
          (VR), Italia — P.IVA 04780750230. Email: agenzia@dentroilcalice.it — PEC: stefcrem@pec.com — Tel. +39 348 261 8512.
          Il Titolare è il punto di contatto per ogni richiesta relativa ai tuoi dati personali. Non è stato nominato un
          Responsabile della Protezione dei Dati (DPO), non ricorrendone i presupposti di obbligatorietà ai sensi dell'art. 37 GDPR.
        </p>
      </Block>

      <Block h="2. Normativa di riferimento">
        <p>Il trattamento dei dati personali è effettuato nel rispetto della normativa vigente, in particolare:</p>
        <ul className="space-y-1">
          <Li>Regolamento (UE) 2016/679 (GDPR) relativo alla protezione delle persone fisiche;</Li>
          <Li>D.Lgs. 30 giugno 2003, n. 196 (Codice in materia di protezione dei dati personali), come modificato dal D.Lgs. 10 agosto 2018, n. 101;</Li>
          <Li>Direttiva 2002/58/CE (ePrivacy) e Provvedimento del Garante per la protezione dei dati personali del 10 giugno 2021 in materia di cookie;</Li>
          <Li>D.Lgs. 9 aprile 2003, n. 70 sul commercio elettronico;</Li>
          <Li>Regolamento (UE) 2022/2065 (Digital Services Act) e Regolamento (UE) 2024/1689 (AI Act), ove applicabili agli strumenti basati su intelligenza artificiale.</Li>
        </ul>
      </Block>

      <Block h="3. Dati che raccogliamo">
        <p>
          Trattiamo i dati che ci fornisci volontariamente tramite il modulo di richiesta diagnosi: nome, cognome, azienda/cantina,
          ruolo, email aziendale, telefono (facoltativo), sito web, dati commerciali indicativi (bottiglie/anno, fascia di fatturato,
          canali utilizzati, software in uso) e il testo relativo al processo commerciale che desideri migliorare. Trattiamo inoltre
          dati tecnici e di navigazione (indirizzo IP, log, cookie tecnici) secondo la Cookie Policy.
        </p>
      </Block>

      <Block h="4. Finalità e basi giuridiche">
        <ul className="space-y-1">
          <Li>Riscontro alla richiesta di diagnosi e misure precontrattuali (art. 6.1.b GDPR);</Li>
          <Li>Gestione della relazione commerciale e contatto (art. 6.1.b/f GDPR);</Li>
          <Li>Invio, previo consenso, di comunicazioni informative e commerciali (art. 6.1.a GDPR, revocabile in ogni momento);</Li>
          <Li>Adempimento di obblighi di legge, fiscali e contabili (art. 6.1.c GDPR);</Li>
          <Li>Tutela dei diritti del Titolare e sicurezza del sito (legittimo interesse, art. 6.1.f GDPR).</Li>
        </ul>
      </Block>

      <Block h="5. Modalità del trattamento e sicurezza">
        <p>
          I dati sono trattati con strumenti elettronici, adottando misure tecniche e organizzative adeguate ai sensi dell'art. 32 GDPR
          (connessioni cifrate HTTPS, accessi protetti, minimizzazione dei dati) per prevenire accessi non autorizzati, perdita o
          divulgazione. Il trattamento è improntato ai principi di liceità, correttezza, trasparenza, limitazione delle finalità,
          minimizzazione ed esattezza (art. 5 GDPR).
        </p>
      </Block>

      <Block h="6. Destinatari e responsabili esterni">
        <p>
          I dati possono essere trattati, per conto del Titolare e in qualità di Responsabili del trattamento (art. 28 GDPR), da
          fornitori selezionati: provider di email marketing e CRM (es. Brevo), infrastruttura di hosting e database del fornitore
          della piattaforma, strumenti di analisi (es. Google Analytics) e di marketing (es. Meta) attivati solo previo consenso ai
          cookie. I dati non sono oggetto di diffusione né di vendita a terzi.
        </p>
      </Block>

      <Block h="7. Trasferimenti extra-UE">
        <p>
          Alcuni fornitori possono trattare dati anche al di fuori dello Spazio Economico Europeo. In tal caso il trasferimento avviene
          con garanzie adeguate previste dagli artt. 44-49 GDPR (es. decisioni di adeguatezza o Clausole Contrattuali Standard della
          Commissione europea).
        </p>
      </Block>

      <Block h="8. Periodo di conservazione">
        <p>
          I dati sono conservati per il tempo strettamente necessario alle finalità indicate: i dati di contatto/lead finché sussiste
          la relazione o fino a richiesta di cancellazione; i dati con rilevanza fiscale/contabile per i termini di legge (di norma 10
          anni). Al termine i dati sono cancellati o resi anonimi.
        </p>
      </Block>

      <Block h="9. Diritti dell'interessato">
        <p>
          Puoi esercitare in ogni momento i diritti di accesso, rettifica, cancellazione ("diritto all'oblio"), limitazione,
          opposizione e portabilità (artt. 15-22 GDPR), oltre a revocare il consenso prestato, scrivendo a agenzia@dentroilcalice.it o
          alla PEC stefcrem@pec.com. Hai inoltre diritto di proporre reclamo al <strong>Garante per la protezione dei dati personali</strong>
          (Piazza Venezia 11, 00187 Roma — www.garanteprivacy.it).
        </p>
      </Block>

      <Block h="10. Processo decisionale automatizzato e profilazione">
        <p>
          Non effettuiamo processi decisionali interamente automatizzati che producano effetti giuridici o significativi sull'interessato
          ai sensi dell'art. 22 GDPR, né attività di profilazione senza idonea base giuridica.
        </p>
      </Block>

      <Block h="11. Minori">
        <p>
          I servizi non sono rivolti a minori di 16 anni; non raccogliamo consapevolmente dati di minori senza il consenso di chi
          esercita la responsabilità genitoriale (art. 8 GDPR).
        </p>
      </Block>

      <Block h="12. Utilizzo di intelligenza artificiale (trasparenza)">
        <p>
          Alcune attività possono avvalersi di strumenti basati su AI di terze parti, utilizzati con finalità operative e sotto controllo
          umano, nel rispetto dei principi di trasparenza del GDPR e del Regolamento (UE) 2024/1689 (AI Act). Tali strumenti non sono
          impiegati per decisioni automatizzate con effetti giuridici sull'interessato e non ricevono categorie particolari di dati.
        </p>
      </Block>

      <Block h="13. Legge applicabile e foro competente">
        <p>
          Il presente trattamento e il rapporto con gli utenti sono regolati dalla legge italiana. Per i rapporti con clienti
          professionali (B2B) è competente in via esclusiva il <strong>Foro di Verona</strong>. Nei confronti dell'utente qualificabile
          come consumatore ai sensi del D.Lgs. 6 settembre 2005, n. 206 (Codice del Consumo) resta ferma la competenza del foro del luogo
          di residenza o domicilio del consumatore, ove inderogabile per legge.
        </p>
      </Block>

      <Block h="14. Modifiche">
        <p>
          La presente informativa può essere aggiornata per adeguamenti normativi o di servizio; la data in alto indica l'ultima
          revisione. Il presente documento costituisce un modello informativo e si raccomanda una verifica legale prima di eventuali
          usi ulteriori.
        </p>
      </Block>
    </LegalLayout>
  );
}

export function CookiePolicy() {
  return (
    <LegalLayout title="Cookie Policy" updated={UPDATED}>
      <Block h="1. Titolare e riferimenti normativi">
        <p>
          Titolare: Stefano Cremonese — P.IVA 04780750230 — agenzia@dentroilcalice.it. La presente Cookie Policy è redatta ai sensi
          dell'art. 122 del D.Lgs. 196/2003, del Regolamento (UE) 2016/679 (GDPR) e del Provvedimento del Garante per la protezione dei
          dati personali del 10 giugno 2021 in materia di cookie e altri strumenti di tracciamento.
        </p>
      </Block>

      <Block h="2. Cosa sono i cookie">
        <p>
          I cookie sono piccoli file di testo salvati sul dispositivo dell'utente. Possono essere utilizzati per il funzionamento del
          sito, per finalità statistiche o di marketing, e possono essere di prima o di terza parte, con durata di sessione o persistente.
        </p>
      </Block>

      <Block h="3. Tipologie di cookie utilizzati">
        <ul className="space-y-1">
          <Li><strong>Cookie tecnici e necessari</strong>: indispensabili al funzionamento del sito e alla memorizzazione delle preferenze di consenso. Non richiedono consenso (art. 122 D.Lgs. 196/2003).</Li>
          <Li><strong>Cookie analitici</strong>: utilizzati, previo consenso, per comprendere l'uso del sito (es. Google Analytics 4).</Li>
          <Li><strong>Cookie di marketing/profilazione</strong>: utilizzati, previo consenso, per finalità pubblicitarie (es. Meta Pixel).</Li>
        </ul>
      </Block>

      <Block h="4. Base giuridica e consenso">
        <p>
          I cookie tecnici si basano sul legittimo interesse del Titolare all'erogazione del servizio. I cookie analitici e di marketing
          non essenziali vengono attivati esclusivamente dopo il consenso espresso dell'utente, prestato tramite il banner di gestione
          del consenso (art. 6.1.a GDPR). Il consenso è libero, specifico, informato e revocabile in qualsiasi momento.
        </p>
      </Block>

      <Block h="5. Gestione delle preferenze">
        <p>
          Puoi modificare o revocare in ogni momento le tue scelte tramite il banner di gestione del consenso presente sul sito, oppure
          cancellando i cookie dalle impostazioni del tuo browser. La disattivazione di alcuni cookie potrebbe limitare alcune
          funzionalità del sito.
        </p>
      </Block>

      <Block h="6. Cookie e strumenti di terze parti">
        <p>
          Previo consenso, il sito può utilizzare strumenti di terze parti (es. Google Analytics, Meta) che trattano i dati secondo le
          rispettive informative. Le comunicazioni commerciali possono essere gestite tramite provider di email marketing (es. Brevo) in
          qualità di Responsabile del trattamento. Eventuali trasferimenti extra-UE avvengono con le garanzie di cui agli artt. 44-49 GDPR.
        </p>
      </Block>

      <Block h="7. Conservazione">
        <p>
          I cookie hanno durata variabile in funzione della loro tipologia (di sessione o persistenti). Le preferenze di consenso sono
          conservate per il periodo previsto dalle Linee guida del Garante e comunque non oltre i termini di legge.
        </p>
      </Block>

      <Block h="8. Diritti, reclamo e foro competente">
        <p>
          Per l'esercizio dei diritti (artt. 15-22 GDPR) e ogni informazione, scrivi a agenzia@dentroilcalice.it. Hai diritto di proporre
          reclamo al Garante per la protezione dei dati personali (www.garanteprivacy.it). Si applica la legge italiana; per i rapporti
          B2B è competente il Foro di Verona, fatta salva la competenza inderogabile del foro del consumatore ai sensi del D.Lgs. 206/2005.
          Il presente documento costituisce un modello e si raccomanda una verifica legale prima della pubblicazione definitiva.
        </p>
      </Block>
    </LegalLayout>
  );
}
